/**
 * Signed-URL layer for private IG media buckets.
 *
 * The IG media buckets (ig-post-thumbs / ig-post-videos / ig-story-thumbs) are
 * private — fetching requires auth. Every stored media URL is a Supabase public
 * URL of the form `…/storage/v1/object/public/<bucket>/<path>`, which still encodes
 * the bucket + path even after the bucket is private. We parse that and mint a
 * short-lived signed URL via the logged-in user's session (RLS: authenticated only).
 *
 * Non-Supabase URLs (raw IG CDN fallbacks like thumbnail_url / media_url) are passed
 * through unchanged — they're already public and can't be signed.
 *
 * Results are cached per source-URL until shortly before expiry, so grids that
 * re-render or reference the same image don't re-sign.
 */
import { useEffect, useState } from 'react';
import { supabase } from './client';

const PUBLIC_RE = /\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/;
const EXPIRY_S = 3600; // 1h signed URL
const REFRESH_BEFORE_MS = 5 * 60 * 1000; // re-sign 5 min before expiry

type Cached = { url: string; expMs: number };
const cache = new Map<string, Cached>();
const inflight = new Map<string, Promise<string>>();

/** Parse a Supabase public storage URL → {bucket, path}; null if not one. */
export function parsePublicUrl(url: string): { bucket: string; path: string } | null {
  const m = url.match(PUBLIC_RE);
  if (!m) return null;
  // strip any query string and decode the object key
  return { bucket: m[1], path: decodeURIComponent(m[2].split('?')[0]) };
}

/** Resolve a (possibly public) media URL to a signed URL. Passthrough for non-Supabase URLs. */
export async function toSignedUrl(url: string | null | undefined): Promise<string | undefined> {
  if (!url) return undefined;
  const parsed = parsePublicUrl(url);
  if (!parsed) return url; // raw IG CDN (or already signed) → leave as-is
  const now = Date.now();
  const hit = cache.get(url);
  if (hit && hit.expMs > now) return hit.url;

  let p = inflight.get(url);
  if (!p) {
    p = (async () => {
      try {
        const { data, error } = await supabase.storage
          .from(parsed.bucket)
          .createSignedUrl(parsed.path, EXPIRY_S);
        const signed = !error && data?.signedUrl ? data.signedUrl : url;
        cache.set(url, { url: signed, expMs: Date.now() + EXPIRY_S * 1000 - REFRESH_BEFORE_MS });
        return signed;
      } catch {
        return url; // network/permission failure → fall back to the original
      } finally {
        inflight.delete(url);
      }
    })();
    inflight.set(url, p);
  }
  return p;
}

function cachedOrPassthrough(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (!parsePublicUrl(url)) return url; // passthrough resolves synchronously
  const hit = cache.get(url);
  return hit && hit.expMs > Date.now() ? hit.url : undefined;
}

/** Hook: resolve a single media URL to a signed (or passthrough) URL. */
export function useSignedUrl(url: string | null | undefined): string | undefined {
  const [out, setOut] = useState<string | undefined>(() => cachedOrPassthrough(url));
  useEffect(() => {
    let on = true;
    if (!url) { setOut(undefined); return; }
    toSignedUrl(url).then((u) => { if (on) setOut(u); });
    return () => { on = false; };
  }, [url]);
  return out;
}

/**
 * Hook: resolve many media URLs at once (grids). Returns a map from the ORIGINAL
 * url → signed url. Look up with `map[original] ?? original`.
 */
export function useSignedUrls(urls: (string | null | undefined)[]): Record<string, string> {
  const uniq = [...new Set(urls.filter(Boolean) as string[])];
  const key = uniq.join('|');
  const [map, setMap] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const u of uniq) {
      const c = cachedOrPassthrough(u);
      if (c) init[u] = c;
    }
    return init;
  });
  useEffect(() => {
    let on = true;
    Promise.all(uniq.map(async (u) => [u, (await toSignedUrl(u)) ?? u] as const))
      .then((pairs) => { if (on) setMap((prev) => ({ ...prev, ...Object.fromEntries(pairs) })); });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return map;
}
